import React, { useEffect, useRef } from 'react';
import { useDrag } from 'react-dnd';

const ItemTypes = { STUDENT: 'student' };

// --- D&D Components ---
export const DraggableStudentCard = ({ student, scrollableContainerRef }) => {
  const animationFrameRef = useRef(null);
  const scrollSettingsRef = useRef({ direction: 0, speed: 0, element: null });

  const [{ isDragging, clientOffset }, drag] = useDrag(() => ({
    type: ItemTypes.STUDENT,
    item: { student },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
      clientOffset: monitor.getClientOffset(),
    }),
  }));
  
  const scrollLoop = () => {
    const { direction, speed, element } = scrollSettingsRef.current;
    if (direction !== 0 && element) {
      element.scrollBy(0, direction * speed);
      animationFrameRef.current = requestAnimationFrame(scrollLoop);
    }
  };

  // This effect handles the auto-scrolling logic for both the container and the window
  useEffect(() => {
    if (!isDragging || !clientOffset) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    let targetElement = window;
    let targetRect;
    let cursorY;
    let isContainedScroll = false;
    
    const container = scrollableContainerRef?.current;
    if (container) {
        const containerRect = container.getBoundingClientRect();
        const isCursorInside = clientOffset.y >= containerRect.top &&
                               clientOffset.y <= containerRect.bottom &&
                               clientOffset.x >= containerRect.left &&
                               clientOffset.x <= containerRect.right;

        if (isCursorInside) {
            targetElement = container;
            targetRect = containerRect;
            cursorY = clientOffset.y - targetRect.top;
            isContainedScroll = true;
        }
    }

    if (!isContainedScroll) {
        targetRect = { top: 0, height: window.innerHeight };
        cursorY = clientOffset.y;
    }

    // Define scroll zones and speeds
    const topZoneHeight = targetRect.height * 0.20 + 30;
    const bottomZoneHeight = targetRect.height * 0.30 + 40; 
 
    const minSpeed = 3;  
    const maxSpeed = isContainedScroll ? 15 : 22;

    let newDirection = 0;
    let newSpeed = 0;

    if (cursorY < topZoneHeight) {
      newDirection = -1;
      const progress = (topZoneHeight - cursorY) / topZoneHeight;
      newSpeed = minSpeed + (maxSpeed - minSpeed) * Math.pow(progress, 2);
    } else if (cursorY > targetRect.height - bottomZoneHeight) {
      newDirection = 1;
      const progress = (cursorY - (targetRect.height - bottomZoneHeight)) / bottomZoneHeight;
      newSpeed = minSpeed + (maxSpeed - minSpeed) * Math.pow(progress, 2);
    }


    scrollSettingsRef.current = { direction: newDirection, speed: newSpeed, element: targetElement };
    
    // Manage animation loop
    if (newDirection !== 0 && !animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(scrollLoop);
    } else if (newDirection === 0 && animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isDragging, clientOffset, scrollableContainerRef]);

  return (
    <div ref={drag} className={`p-3 bg-white dark:bg-navy-700 rounded-lg shadow-sm cursor-grab active:cursor-grabbing flex justify-between items-center ${isDragging ? 'opacity-40' : 'opacity-100'}`}>
      <div>
        <p className="font-semibold text-base text-navy-700 dark:text-white">{student.firstName} {student.lastName}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400">@{student.username}</p>
      </div>
    </div>
  );
};